import React from "react";
import { useNavigate } from "react-router-dom";
import { Shield } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import { centsToUSD, shortDate } from "@/lib/dates";
import { TIERS } from "@/lib/pricing";
import { cn } from "@/lib/utils";

const tierBadgeStyle = (tier) => {
  if (tier === "gold") return { background: "var(--color-tier-gold-bg-15)", border: "1px solid var(--color-tier-gold-border-40)", color: "var(--color-tier-gold)" };
  if (tier === "silver") return { background: "rgba(192, 192, 192, 0.15)", border: "1px solid rgba(192, 192, 192, 0.40)", color: "var(--color-tier-silver)" };
  return { background: "rgba(205, 127, 50, 0.15)", border: "1px solid rgba(205, 127, 50, 0.40)", color: "var(--color-tier-bronze)" };
};

const TierBadge = ({ tier, testid }) => {
  const info = TIERS.find((t) => t.key === tier) || TIERS[0];
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-[0.08em]"
      style={tierBadgeStyle(tier)}
      data-testid={testid}
    >
      <Shield size={12} strokeWidth={2.5} />
      {info.label}
    </span>
  );
};

/**
 * Reusable full-width debtor card (Standard's default row shape).
 * Used on the Standard dashboard, Standard My Debtors page, and PRO Guided Mode.
 */
const DebtorRowCard = ({ debtor, onOpen, onRunQuery, onViewReports, onEditTier }) => {
  const nav = useNavigate();
  const open = () => (onOpen ? onOpen(debtor) : nav(`/debtors/${debtor.id}`));
  return (
    <JeaCard
      className="flex items-center gap-6 hover:border-accent-pink/40 transition-colors"
      data-testid={`debtor-card-${debtor.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={open}
            className="text-white font-bold text-[16px] hover:text-accent-pink transition-colors text-left"
            data-testid={`debtor-name-${debtor.id}`}
          >
            {debtor.full_name}
          </button>
          <TierBadge tier={debtor.tier} testid={`debtor-tier-${debtor.id}`} />
        </div>
        <div className="text-[13px] text-text-secondary mt-1">
          Case <span className="jea-caseno">{debtor.judgment?.case_number || "—"}</span>
        </div>
        <div className="grid grid-cols-3 gap-6 mt-3 text-[13px]">
          <div>
            <div className="jea-caption">Judgment</div>
            <div className="jea-mono text-accent-pink font-bold text-[15px]" data-testid={`debtor-amount-${debtor.id}`}>
              {centsToUSD(debtor.judgment?.amount_cents)}
            </div>
          </div>
          <div>
            <div className="jea-caption">Last Query</div>
            <div className="jea-date">{shortDate(debtor.last_query_at)}</div>
          </div>
          <div>
            <div className="jea-caption">Next Query</div>
            <div className="jea-date" data-testid={`debtor-next-query-${debtor.id}`}>
              {shortDate(debtor.next_query_at)}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        {onRunQuery && (
          <JeaButton size="sm" onClick={onRunQuery} data-testid={`debtor-run-query-${debtor.id}`}>
            Run Query
          </JeaButton>
        )}
        <JeaButton size="sm" variant="outline-pink" onClick={onViewReports} data-testid={`debtor-view-reports-${debtor.id}`}>
          View Reports
        </JeaButton>
        {onEditTier && (
          <JeaButton size="sm" variant="outline-neutral" onClick={onEditTier} data-testid={`debtor-edit-tier-${debtor.id}`}>
            Edit Profile
          </JeaButton>
        )}
      </div>
    </JeaCard>
  );
};

export { TierBadge };
export default DebtorRowCard;
